import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  Grid,
  GridItem,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuGroup,
  Skeleton,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import _ from 'lodash';
import React, { useState } from 'react';

import { Receivable, Util } from '@finley-technologies/finley-types';

import {
  useGetLoanProductsForCreditFacilityQuery,
  useGetReceivableResponseTapePreviewCsvDownloadUrlLazyQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  errorToastId,
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { FiDownload } from 'react-icons/fi';

const getFilename = ({
  fileNamePrefix,
  previewType,
  reportingDate,
  fileVersion,
  loanProductSlug,
  extension = 'csv',
}: {
  fileNamePrefix: string;
  previewType: Receivable.Preview.ReceivableResponseTapePreviewAttachmentType;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  loanProductSlug: string;
  extension?: string;
}) => {
  return `${
    fileNamePrefix ? `${fileNamePrefix}-` : ''
  }${loanProductSlug}-${previewType}-${reportingDate}-v${fileVersion}.${extension}`;
};

const OptionalMenuGroup: React.FC<{
  children: React.ReactNode;
  showGrouping: boolean;
  menuGroupLabel: string;
}> = ({ children, showGrouping, menuGroupLabel }) => {
  if (showGrouping) {
    return <MenuGroup title={menuGroupLabel}>{children}</MenuGroup>;
  }
  return <>{children}</>;
};

const DownloadPreviewMenuItem: React.FC<{
  fileNamePrefix: string;
  previewType: Receivable.Preview.ReceivableResponseTapePreviewAttachmentType;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  loanProductSlug: string;
  loanProductId: string;
  loanProductName: string;
  creditFacilityId: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  previewIdentifier: string;
}> = ({
  fileNamePrefix,
  previewType,
  reportingDate,
  fileVersion,
  loanProductSlug,
  loanProductId,
  loanProductName,
  creditFacilityId,
  previewIdentifier,
  setLoading,
}) => {
  const fileName = getFilename({
    fileNamePrefix,
    previewType,
    reportingDate,
    fileVersion,
    loanProductSlug,
  });
  const toast = useToast();
  const [
    getReceivableResponseTapePreviewCsvDownloadUrl,
  ] = useGetReceivableResponseTapePreviewCsvDownloadUrlLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (response && response.getReceivableResponseTapePreviewCsvDownloadUrl) {
        const {
          getReceivableResponseTapePreviewCsvDownloadUrl: url,
        } = response;
        axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((res) => {
            const fileBlob = new Blob([res.data], {
              type: res.data.type,
            });
            FileSaver.saveAs(fileBlob, fileName);
            successToastConfig.title = `Successfully downloaded ${fileName}`;
            toast(successToastConfig);
            setLoading(false);
          })
          .catch((err) => {
            if (!toast.isActive(errorToastId)) {
              errorToastConfig.title = `Error downloading ${fileName}`;
              toast(errorToastConfig);
            }
            Sentry.captureException(err);
          });
      }
    },
  });

  return (
    <MenuItem
      onClick={() => {
        setLoading(true);
        Sentry.addBreadcrumb({
          category: 'ui.click',
          message: `Clicked download for ${fileName} in assignment summary`,
          level: Sentry.Severity.Info,
        });
        getReceivableResponseTapePreviewCsvDownloadUrl({
          variables: {
            creditFacilityId,
            loanProductId,
            reportingDate,
            fileVersion,
            type: previewType,
            previewIdentifier,
          },
        });
      }}
    >
      <Grid templateColumns="repeat(4, 1fr)" w="100%">
        <GridItem colSpan={3} textAlign="left">
          {`${loanProductName} ${_.startCase(previewType)}`}
        </GridItem>
        <GridItem colSpan={1} textAlign="right">
          <Icon fontSize="20px">
            <FiDownload />
          </Icon>
        </GridItem>
      </Grid>
    </MenuItem>
  );
};

export const DownloadPreviewButton = ({
  creditFacilityId,
  fileVersion,
  reportingDate,
  buttonLabel,
  fileNamePrefix,
  previewIdentifier,
}: {
  creditFacilityId: string;
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  fileNamePrefix?: string;
  buttonLabel?: string;
  previewIdentifier: string;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { data } = useGetLoanProductsForCreditFacilityQuery({
    variables: { creditFacilityId, useWip: false },
    fetchPolicy: 'network-only',
  });

  if (!data || !data.creditFacility) {
    return (
      <Skeleton
        id="assignment-download-preview-button-loading"
        height="32px"
        width="200px"
      />
    );
  }

  const { loanProducts } = data.creditFacility;

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        variant="outline"
        colorScheme="aurora"
        border="2px solid"
        isLoading={loading}
      >
        {buttonLabel || 'Download Preview'}
      </MenuButton>
      <MenuList>
        {_.map(
          loanProducts,
          ({
            id: loanProductId,
            name: loanProductName,
            slug: loanProductSlug,
          }) => (
            <OptionalMenuGroup
              key={loanProductId}
              showGrouping={loanProducts.length > 1}
              menuGroupLabel={loanProductName}
            >
              <DownloadPreviewMenuItem
                creditFacilityId={creditFacilityId}
                fileNamePrefix={fileNamePrefix || ''}
                previewType="extendedResponseTapePreview"
                reportingDate={reportingDate}
                fileVersion={fileVersion}
                loanProductId={loanProductId}
                loanProductName={loanProductName}
                loanProductSlug={loanProductSlug}
                setLoading={setLoading}
                previewIdentifier={previewIdentifier}
              />
              <DownloadPreviewMenuItem
                creditFacilityId={creditFacilityId}
                fileNamePrefix={fileNamePrefix || ''}
                previewType="proposedExtendedResponseTapePreview"
                reportingDate={reportingDate}
                fileVersion={fileVersion}
                loanProductId={loanProductId}
                loanProductName={loanProductName}
                loanProductSlug={loanProductSlug}
                setLoading={setLoading}
                previewIdentifier={previewIdentifier}
              />
            </OptionalMenuGroup>
          ),
        )}
      </MenuList>
    </Menu>
  );
};
